import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

import { ListYourHorseLink, NamedLink } from '../../components';

import css from './FooterContainer.module.css';

const ChevronUp = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 15 12 9 18 15" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/**
 * Site footer — a persistent minimal bottom strip that opens a slide-up
 * bottom-sheet drawer with the full footer. Used on every page (rendered via
 * the shared `footer` slot in the page layouts). The drawer reuses the Topbar's
 * accessibility conventions: portal + focus-trap + inert-when-closed + ESC +
 * backdrop-tap to close.
 */
const FooterContainer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock body scroll while the sheet is open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus trap + initial focus.
  useEffect(() => {
    if (!isOpen || !panelRef.current) return undefined;
    const panel = panelRef.current;
    const sel =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const els = Array.from(panel.querySelectorAll(sel));
    if (!els.length) return undefined;
    els[0].focus();
    const trap = e => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === els[0]) {
        e.preventDefault();
        els[els.length - 1].focus();
      } else if (!e.shiftKey && document.activeElement === els[els.length - 1]) {
        e.preventDefault();
        els[0].focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [isOpen]);

  // Prevent keyboard focus reaching the offscreen panel when closed.
  useEffect(() => {
    if (!panelRef.current) return;
    if (isOpen) {
      panelRef.current.removeAttribute('inert');
    } else {
      panelRef.current.setAttribute('inert', '');
    }
  }, [isOpen, mounted]);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const drawerPortal = mounted
    ? ReactDOM.createPortal(
        <>
          <div
            className={classNames(css.backdrop, { [css.backdropVisible]: isOpen })}
            onClick={close}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            id="site-footer-drawer"
            className={classNames(css.footerDrawer, { [css.drawerOpen]: isOpen })}
            role="dialog"
            aria-modal="true"
            aria-label="About, help and more"
            aria-hidden={!isOpen}
          >
            <button type="button" className={css.drawerClose} onClick={close} aria-label="Close">
              <CloseIcon />
            </button>

            <div className={css.grabHandle} aria-hidden="true" />
            <div className={css.ribbon} aria-hidden="true" />

            <div className={css.cols}>
              {/* Pre-launch dead-link sweep: every '#' placeholder is removed
                  (social icons, The Market, Get alerts, the whole Company and
                  Resources sections). Links return one-by-one as pages go live. */}
              <div className={css.brandCol}>
                <p className={css.selling}>Selling excellence</p>
                <p className={css.mission}>
                  The leading platform where the industry goes to buy, sell, and advertise premier
                  horses.
                </p>
              </div>

              <div className={css.col}>
                <h4>Explore</h4>
                <NamedLink name="SearchPage">Horses for sale</NamedLink>
                <ListYourHorseLink>List your horse</ListYourHorseLink>
              </div>
            </div>

            <div className={css.legalRow}>
              <span>© The Premier Horse 2026</span>
              <span>
                <NamedLink name="TermsOfServicePage" className={css.legalLink}>
                  Terms
                </NamedLink>
                {'  ·  '}
                <NamedLink name="PrivacyPolicyPage" className={css.legalLink}>
                  Privacy
                </NamedLink>
                {'  ·  '}
                <NamedLink name="ContactPage" className={css.legalLink}>
                  Contact
                </NamedLink>
              </span>
            </div>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <footer className={css.footerStrip}>
      <div className={css.legalLeft}>
        <span>© The Premier Horse 2026</span>
        <NamedLink name="TermsOfServicePage" className={css.stripLink}>
          Terms
        </NamedLink>
        <NamedLink name="PrivacyPolicyPage" className={css.stripLink}>
          Privacy
        </NamedLink>
        <NamedLink name="ContactPage" className={css.stripLink}>
          Contact
        </NamedLink>
      </div>

      <div className={css.stripRight}>
        <button
          ref={triggerRef}
          type="button"
          className={css.aboutMore}
          onClick={open}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls="site-footer-drawer"
        >
          About, help &amp; more
          <ChevronUp />
        </button>
      </div>

      {drawerPortal}
    </footer>
  );
};

export default FooterContainer;
